import { lazy, Suspense } from 'react';
import { Loading } from '@xanectech/components/shared';

// Lazy-loaded pages for code splitting
const Index = lazy(() => import('@/pages/Index'));
const ProductPage = lazy(() => import('@/pages/ProductPage'));
const PlaceholderPage = lazy(() => import('@/pages/PlaceholderPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const BlogPage = lazy(() => import('@/pages/BlogPage'));
const BlogPostPage = lazy(() => import('@/pages/BlogPostPage'));
const DocsPage = lazy(() => import('@/pages/DocsPage'));
type NotFoundComponent = React.ComponentType<{ showDashboard?: boolean }>;

const isNotFoundModule = (
  value: unknown,
): value is { NotFoundPage: NotFoundComponent } => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const moduleRecord = value as Record<string, unknown>;
  return typeof moduleRecord.NotFoundPage === 'function';
};

const NotFound = lazy(() =>
  import('@xanectech/components/shared').then((moduleValue) => {
    if (isNotFoundModule(moduleValue)) {
      return {
        default: moduleValue.NotFoundPage,
      };
    }

    return {
      default: () => <PlaceholderPage />,
    };
  }),
);
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('@/pages/TermsOfServicePage'));
const SecurityAnimationPage = lazy(
  () => import('@/pages/SecurityAnimationPage'),
);
const SecurityProcessPage = lazy(() => import('@/pages/SecurityProcessPage'));
const YourRightsAnimationPage = lazy(
  () => import('@/pages/YourRightsAnimationPage'),
);

export type RouteType = {
  path: string;
  element: React.ReactElement;
};

// Loading fallback component
const RouteLoadingFallback = () => <Loading spinner='Xanec' />;

export const routes = (): RouteType[] => {
  return [
    {
      path: '/',
      element: (
        <Suspense fallback={<RouteLoadingFallback />}>
          <Index />
        </Suspense>
      ),
    },
    {
      path: '/products/:productId',
      element: (
        <Suspense fallback={<RouteLoadingFallback />}>
          <ProductPage />
        </Suspense>
      ),
    },
    {
      path: '/solutions/:solutionId',
      element: (
        <Suspense fallback={<RouteLoadingFallback />}>
          <PlaceholderPage />
        </Suspense>
      ),
    },
    {
      path: '/docs',
      element: (
        <Suspense fallback={<RouteLoadingFallback />}>
          <DocsPage />
        </Suspense>
      ),
    },

    {
      path: '/privacy-policy',
      element: (
        <Suspense fallback={<RouteLoadingFallback />}>
          <PrivacyPolicyPage />
        </Suspense>
      ),
    },
    {
      path: '/terms-of-service',
      element: (
        <Suspense fallback={<RouteLoadingFallback />}>
          <TermsOfServicePage />
        </Suspense>
      ),
    },

    {
      path: '/customer-stories',
      element: (
        <Suspense fallback={<RouteLoadingFallback />}>
          <PlaceholderPage />
        </Suspense>
      ),
    },
    {
      path: '/blog',
      element: (
        <Suspense fallback={<RouteLoadingFallback />}>
          <BlogPage />
        </Suspense>
      ),
    },
    {
      path: '/blog/:postId',
      element: (
        <Suspense fallback={<RouteLoadingFallback />}>
          <BlogPostPage />
        </Suspense>
      ),
    },
    {
      path: '/about',
      element: (
        <Suspense fallback={<RouteLoadingFallback />}>
          <AboutPage />
        </Suspense>
      ),
    },
    {
      path: '/careers',
      element: (
        <Suspense fallback={<RouteLoadingFallback />}>
          <PlaceholderPage />
        </Suspense>
      ),
    },
    {
      path: '/contact',
      element: (
        <Suspense fallback={<RouteLoadingFallback />}>
          <ContactPage />
        </Suspense>
      ),
    },
    {
      path: '/privacy',
      element: (
        <Suspense fallback={<RouteLoadingFallback />}>
          <PlaceholderPage />
        </Suspense>
      ),
    },
    {
      path: '/terms',
      element: (
        <Suspense fallback={<RouteLoadingFallback />}>
          <PlaceholderPage />
        </Suspense>
      ),
    },

    {
      path: '/security-animation',
      element: (
        <Suspense fallback={<RouteLoadingFallback />}>
          <SecurityAnimationPage />
        </Suspense>
      ),
    },
    {
      path: '/security-process',
      element: (
        <Suspense fallback={<RouteLoadingFallback />}>
          <SecurityProcessPage />
        </Suspense>
      ),
    },
    {
      path: '/your-rights-animation',
      element: (
        <Suspense fallback={<RouteLoadingFallback />}>
          <YourRightsAnimationPage />
        </Suspense>
      ),
    },

    {
      path: '*',
      element: (
        <Suspense fallback={<RouteLoadingFallback />}>
          <NotFound showDashboard />
        </Suspense>
      ),
    },
  ];
};
export default routes;
